import { Association } from '@/models/businesses/association';
import { Business } from '@/models/businesses/business';
import { BusinessActivity } from '@/models/businesses/business-activity';
import { GenericResult } from '@/models/common/results/generic-result';
import { UpdateAdminResult } from '@/models/common/results/update-admin-result';
import { UpdateCompanyResult } from '@/models/common/results/update-company-result';
import { Company } from '@/models/companies/company';
import { UserEntityPermissionsType } from '@/models/users/user-permissions/user-entity-permissions';
import { axiosService } from '.';
import { BusinessType } from '@/models/businesses/business-type';
import { NewSpaceData } from '@/models/shared/new-space-data';

export default class BusinessesService {
    public async getBusiness(id: number): Promise<Business> {
        return new Business((await axiosService.instance.get(`businesses/organization/${id}`)).data);
    }

    public async getBusinesses(): Promise<Business[]> {
        const response = await axiosService.instance.get('businesses/organization/all');
        return response.data.map((business: any) => new Business(business));
    }

    public async getBusinessByProjectId(projectId: number): Promise<Business[]> {
        const response = await axiosService.instance.get(`businesses/organization/by-project/${projectId}`);
        return response.data.map((business: any) => new Business(business));
    }

    public async getBusinessByCampaigId(campaignId: number): Promise<Business[]> {
        const response = await axiosService.instance.get(`businesses/organization/by-campaign/${campaignId}`);
        return response.data.map((business: any) => new Business(business));
    }

    public async getBusinessByTypeAndProjectId(type: BusinessType, projectId: number): Promise<Business[]> {
        const response = await axiosService.instance.get(`businesses/organization/by-type-and-project/${type}/${projectId}`);
        return response.data.map((business: any) => new Business(business));
    }

    public async getBusinessByTypeAndProjectIds(type: BusinessType, projectIds: number[]): Promise<Business[]> {
        const projectIdsQueryString = 'projectIds=' + projectIds.join('&projectIds=');
        const response = await axiosService.instance.get(`businesses/organization/by-type-and-projects/${type}?${projectIdsQueryString}`);
        return response.data.map((business: any) => new Business(business));
    }

    public async getAssociations(projectIds: number[]): Promise<Association[]> {
        const projectIdsQueryString = 'projectIds=' + projectIds.join('&projectIds=');
        const response = await axiosService.instance.get(`associations/organization/by-projects?${projectIdsQueryString}`);
        return response.data.map((association: any) => new Association(association));
    }

    public async getActivities(): Promise<BusinessActivity[]> {
        const response = await axiosService.instance.get('businesses/organization/activities');
        return response.data.map((activity: any) => new BusinessActivity(activity));
    }

    public async newBusiness(business: Business): Promise<GenericResult> {
        const newBusinessData = {
            tradeName: business.tradeName,
            type: business.type,
            projectId: business.projects.map(p => p.id)[0],
            pricingId: business.pricings.map(p => p.id)[0],
            linkedBusinessId: business.linkedBusinesses && business.linkedBusinesses[0] ? business.linkedBusinesses[0].id : null,
            associationId: business.associations && business.associations[0] ? business.associations[0].associationId : null,
        };
        return new GenericResult((await axiosService.instance.post('businesses/organization/new', newBusinessData)).data);
    }

    public async newBusinessThroughProspect(businessProspectId: number): Promise<GenericResult> {
        return new GenericResult((await axiosService.instance.post(`businesses/organization/new-through-prospect?businessProspectId=${businessProspectId}`)).data);
    }

    public async updateBusinessBasicData(business: Business): Promise<boolean> {
        const newBusinessBasicData = {
            id: business.id,
            tradeName: business.tradeName,
            associations: business.associations,
            topLevelBusinesses: business.topLevelBusinesses,
            mainActivity: business.mainActivity === undefined ? null : { id: business.mainActivity!.id },
            secondaryActivity: business.secondaryActivity === undefined ? null : { id: business.secondaryActivity!.id }
        };

        return (await axiosService.instance.post('businesses/organization/update-basic-data', newBusinessBasicData)).data;
    }

    public async updateBusinessProjects(business: Business): Promise<boolean> {
        const newBusinessProjectsData = {
            id: business.id,
            projects: business.projects.map(p => ({ id: p.id, zoneId: p.zoneId })),
            pricingIds: business.pricings.map(p => p.id)
        };

        return (await axiosService.instance.post('businesses/organization/update-projects-data', newBusinessProjectsData)).data;
    }

    public async updateBusinessCompany(id: number, company: Company): Promise<UpdateCompanyResult> {
        return (await axiosService.instance.post(`businesses/organization/update-company/${id}`, company)).data;
    }

    public async updateBusinessOwner(businessId: number, ownerId: number, ownerEmail?: string): Promise<UpdateAdminResult> {
        return (await axiosService.instance.post(`businesses/organization/update-owner/${businessId}/${ownerId}?ownerEmail=${ownerEmail}`)).data;
    }

    public async updateBusinessInvitedOwnerEmail(businessId: number, invitedOwnerEmail: string): Promise<UpdateAdminResult> {
        return (await axiosService.instance.post(`businesses/organization/update-invited-owner-email/${businessId}?invitedOwnerEmail=${invitedOwnerEmail}`)).data;
    }

    public async deleteBusiness(businessId: number): Promise<any> {
        return (await axiosService.instance.post(`businesses/organization/${businessId}/disable`)).data;
    }

    public async getBusinessFrontPageFormConfig(businessId: number): Promise<GetBusinessFormConfigOutputDto> {
        const data = (await axiosService.instance.get(`businesses/organization/${businessId}/form-config`)).data;
        return new GetBusinessFormConfigOutputDto(data.visible, data.notes);
    }

    public async updateBusinessFrontPageFormConfig(data: UpdateBusinessFormConfigInputDto): Promise<void> {
        return (await axiosService.instance.post(`businesses/organization/update-form-config`, data)).data;
    }

    public async getBusinessFrontPageFormConfigSummary(businessId: number): Promise<GetBusinessFormConfigOutputDtoSummary> {
        const data = (await axiosService.instance.get(`businesses/organization/${businessId}/form-config/summary`)).data;
        return new GetBusinessFormConfigOutputDtoSummary(data.businessId, data.visible);
    }

    public async updateBusinessCanonicalConsumer(businessId: number, newCanonicalConsumerId: number): Promise<boolean> {
        return (await axiosService.instance.post(`businesses/organization/${businessId}/update-canonical-consumer?consumerId=${newCanonicalConsumerId}`)).data;
    }

    public async hasBusinessNewMessages(businessId: number) {
        return (await axiosService.instance.get(`messaging/organization/messages/${businessId}/has-new`)).data;
    }

    public async newSmartAdministrationSpace(spaceData: NewSpaceData): Promise<boolean> {
        return (await axiosService.instance.post('system/smart-administration/new-space', spaceData)).data;
    }

    public async canActualUserAccessDinamizationApp(): Promise<boolean> {
        return (await axiosService.instance.get('businesses/organization/can-actual-user-access-dinamization-app')).data;
    }

    public async canActualUserAccessLoyaltyApp(): Promise<boolean> {
        return (await axiosService.instance.get('businesses/organization/can-actual-user-access-loyalty-app')).data;
    }
}

export class GetBusinessFormConfigOutputDto {
    public readonly visible: boolean = false;
    public readonly notes: string = '';

    constructor(visible: boolean, notes: string) {
        this.visible = visible;
        this.notes = notes;
    }
}

export class UpdateBusinessFormConfigInputDto {
    public businessId: number = 0;
    public visible: boolean = false;
    public notes: string = '';

    constructor(businessId: number, visible: boolean, notes: string) {
        this.businessId = businessId;
        this.visible = visible;
        this.notes = notes;
    }
}

export class GetBusinessFormConfigOutputDtoSummary {
    public readonly businessId: number = 0;
    public readonly visible: boolean = false;

    public get permissionsGroupType(): UserEntityPermissionsType {
        return UserEntityPermissionsType.Businesses;
    }

    public get permissionsIdToCheck(): number {
        return this.businessId;
    }

    constructor(businessId: number, visible: boolean) {
        this.businessId = businessId;
        this.visible = visible;
    }
}

export const businessesService = new BusinessesService();
